import { gql } from '@apollo/client';
import { FormLabel, Stack, useToast } from '@chakra-ui/react';
import { cloneDeep, isNil, merge } from 'lodash';
import { toAvailableControlToggleOptions } from '~components/displays/utils';
import {
  FormLabelPendingIndicator,
  isPropertyPending,
} from '~components/ui/FormLabelPendingIndicator';
import { ControlLockState } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { DisplayControlsToggle, ToggleOptionsConfig } from '../DisplayControlsToggle';
import {
  DisplayInfraRedControlToggle_DisplayFragment,
  useUpdateInfraRedControlMutation,
} from './__generated__/DisplayInfraRedControlToggle.graphql';

interface Props {
  display: DisplayInfraRedControlToggle_DisplayFragment;
}

const infraRedControlToggleOptions: Array<ToggleOptionsConfig<ControlLockState>> = [
  { value: ControlLockState.Locked, label: 'Lock all inputs' },
  { value: ControlLockState.VolumeOnly, label: 'Only allow changes to volume' },
  { value: ControlLockState.PowerOnly, label: 'Only allow changes to power' },
  { value: ControlLockState.Unlocked, label: 'Unlock all inputs' },
];

export function DisplayInfraRedControlToggle({ display }: Props) {
  const toast = useToast();
  const [updateInfraRedState, { loading }] = useUpdateInfraRedControlMutation();

  const handleChange = async (controlState: ControlLockState) => {
    const { id, infraRedControl } = display;

    try {
      updateInfraRedState({
        variables: {
          input: {
            displayIds: [id],
            controlState,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          displayBulkUpdateInfraRedControl: {
            __typename: 'DisplayBulkUpdateInfraRedControlPayload',
            displays: [
              {
                __typename: 'Display',
                id,
                infraRedControl: merge(cloneDeep(infraRedControl), {
                  desired: controlState,
                }),
              },
            ],
          },
        },
      });
    } catch (error) {
      toast({
        status: 'error',
        title: 'Cannot update the control state',
        description: fromError(error, 'UpdateInfraRedControl'),
      });
    }
  };

  const currentControlState = display.infraRedControl?.desired ?? display.infraRedControl?.reported;

  const controlOptions = toAvailableControlToggleOptions(
    infraRedControlToggleOptions,
    display.infraRedControl?.supportedValues,
  );

  const isPending = isPropertyPending(display.infraRedControl);

  if (isNil(currentControlState)) {
    return null;
  }

  return (
    <Stack direction="column" spacing="3">
      <FormLabel>
        Lock remote control
        <FormLabelPendingIndicator isPending={isPending} />
      </FormLabel>

      <DisplayControlsToggle
        name="infraRedControlState"
        value={currentControlState}
        handleChange={handleChange}
        isLoading={loading}
        options={controlOptions}
      />
    </Stack>
  );
}

DisplayInfraRedControlToggle.graphql = {
  fragments: {
    DisplayInfraRedControlToggle_display: gql`
      fragment DisplayInfraRedControlToggle_display on Display {
        id
        infraRedControl {
          desired
          reported
          supportedValues
        }
      }
    `,
  },
  mutations: {
    UpdateInfraRedControl: gql`
      mutation UpdateInfraRedControl($input: DisplayBulkUpdateInfraRedControlInput!) {
        displayBulkUpdateInfraRedControl(input: $input) {
          displays {
            id
            infraRedControl {
              desired
              reported
              supportedValues
            }
          }
        }
      }
    `,
  },
};
